import React, {useContext, useEffect, useState} from "react";

// Services
import {useParams} from "react-router-dom";

// UI
import Placeholder from "@ui-components/Placeholder";
import { PageHeading } from "@ui-components/Container";
import {MapPinIcon} from "@heroicons/react/24/solid";
import {FormattedMessage, useIntl} from "react-intl";
import CountryContext from "../../CountryContext";
import Button from "../../ui-components/Button";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import {NotificationsContext} from "../../ui-components/Notifications";
import {ListBulletIcon, MapIcon} from "@heroicons/react/24/outline";
import TaskSection from "../../components/WorksiteTask/TaskSection";
import MapsSection from "../../components/WorksiteTask/MapsSection";
import useTaskStats from "../../components/WorksiteTask/hooks/useTaskStats";
import DonutChart from "../../components/Charts/DonutChart";
import * as am5 from "@amcharts/amcharts5"
import {useMediaQuery} from "../../hooks/useMediaQuery";


const SectionButton = ({ name, section, setSection, children }) => (
  <Button
    full
    styleType={section === name ? 'default' : 'empty'}
    onClick={() => { setSection(name); }}
    className={section === name ? 'cursor-auto pointer-events-none' : ''}
  >
    {children}
  </Button>
)


const WorksitesTasks = () => {

  // params
  const { worksite_id, task_id, map_id, activity_id, note_id } = useParams();

  // context
  const intl = useIntl();
  const { country } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);

  // state
  const [worksite, setWorksite] = useState(null);
  const [section, setSection] = useState(!!map_id ? 'maps' : 'tasks');

  // hooks
  const countryApiRequest = useCountryAxiosInstance(country.code);
  const {stats, refreshStats} = useTaskStats(worksite_id);
  const isWindowMoreThanSM = useMediaQuery('(min-width: 640px)');

  // utilities
  const parsedActivityNoteProps = {
    activity_id: !isNaN(activity_id) ? parseInt(activity_id) : undefined,
    note_id: !isNaN(note_id) ? parseInt(note_id) : undefined,
  };

  useEffect(() => {
    countryApiRequest.get(`/worksites/${worksite_id}`)
      .then(setWorksite)
      .catch((error) => {
        push({type: 'error', title: intl.formatMessage({id: 'server_error'})});
        throw error;
      })
  }, [worksite_id, countryApiRequest]);  // eslint-disable-line react-hooks/exhaustive-deps

  return <>
    <PageHeading
      title={intl.formatMessage({ id: "worksite_tasks" })}
      historyBackPath={`/${country.code}/worksites/${worksite_id}`}
      contentActions={<>
        <div className="flex items-center gap-x-2 text-lg">
          <MapPinIcon className="w-5 h-5" />
          {!!worksite
            ? <span>{`${worksite.address} - ${worksite.city}`.toUpperCase()}</span>
            : <Placeholder width="w-44" classNames="mt-2" />}
        </div>
      </>}
    />

    <div className="border-am-700 border-t" />

    {/* ----- ACTIONS ----- */}
    <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between my-4">
      <div className="flex flex-col sm:flex-row gap-x-4 gap-y-2">
        {!!worksite
          ? <>
            <SectionButton name='tasks' {...{section, setSection}}>
              <ListBulletIcon className="w-4" />
              <span className="whitespace-nowrap">
                <FormattedMessage id="tasks_list" />
              </span>
            </SectionButton>
            <SectionButton name='maps' {...{section, setSection}}>
              <MapIcon className="w-4" />
              <span className="whitespace-nowrap">
                <FormattedMessage id="task_maps" />
              </span>
            </SectionButton>
          </>
          : Array(2).fill(null).map((_, idx) =>
            <Placeholder
              key={idx}
              width="w-32"
              height="h-8"
              classNames="rounded-full"
            />
          )
        }
      </div>
      <div className="flex sm:justify-end">
        {!!stats
          ? <div className="w-full sm:w-40">
            <DonutChart
              id="task-stats"
              data={stats.map(({status, tasks_count}) => ({
                category: intl.formatMessage({id: `task_status_${status}`}),
                value: tasks_count,
              }))}
              minHeight={isWindowMoreThanSM ? "75px" : "150px"}
              legend={!isWindowMoreThanSM}
              legendPosition={!isWindowMoreThanSM ? "bottom" : "left"}
              extraOptions={isWindowMoreThanSM
                ? {centerX: am5.percent(-50), dx: -32}
                : {}
              }
              showPercentages={false}
            />
          </div>
          : <Placeholder width="w-12" height="h-12" classNames="rounded-full" />
        }
      </div>
    </div>

    {/* ---------- SECTIONS ---------- */}
    {worksite && section === 'tasks' && <TaskSection {...{worksite, task_id, refreshStats, ...parsedActivityNoteProps}} />}
    {worksite && section === 'maps' && <MapsSection {...{worksite, map_id, refreshStats}} />}

  </>
};

export default WorksitesTasks;
